import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  DELIVERABLE_GET,
  DELIVERABLE_LIST,
  createDeliverable,
  appendDeliverableRevision,
  deleteDeliverable,
  getDeliverableById,
  getDeliverables,
  rebuildDeliverable,
  updateDeliverable,
} from "@/api/client/deliverable";
import { enqueueDeliverableBuild } from "@/api/client/deliverable-agent";
import {
  Deliverable,
  DeliverableRevisionPayload,
  DeliverableListItem,
  DeliverableListParams,
  UpdateDeliverablePayload,
} from "@/types";
import { showErrorToast } from "@/lib/errorUtils";

/**
 * TanStack Query hooks for the persisted side of Deliverables (focalmonolith).
 *
 * Nothing here touches the advisor's uploaded source *files* — those live only
 * in the in-memory build store (`@/store/deliverableBuild`) and die with the
 * screen. The text extracted from them is persisted against the deliverable and
 * read back through the client layer's `getDeliverableSources`, deliberately not
 * a hook: the corpus is megabytes and is fetched imperatively at the one moment
 * something is about to use it, never as a subscription a viewer pays for.
 *
 * Query keys mirror the client-layer constants so `[DELIVERABLE_LIST]` acts as
 * a prefix that invalidates every scope/search variant of the list at once.
 */

/** Flat, name-searchable list. Pass `scope` to switch between mine / shared. */
export const useDeliverables = (
  params?: DeliverableListParams,
  options?: { enabled?: boolean },
) =>
  useQuery<DeliverableListItem[]>({
    queryKey: [DELIVERABLE_LIST, params ?? {}],
    queryFn: () => getDeliverables(params),
    enabled: options?.enabled ?? true,
  });

/** The full record, including outline and figures. */
export const useDeliverable = (id: string) =>
  useQuery<Deliverable>({
    queryKey: [DELIVERABLE_GET, id],
    queryFn: () => getDeliverableById(id),
    enabled: !!id,
  });

/**
 * `POST /deliverable` — called once a build completes. Navigation is left to
 * the caller so the build screen can decide when to leave the streaming view.
 */
export const useCreateDeliverable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDeliverable,
    onSuccess: (data) => {
      queryClient.setQueryData([DELIVERABLE_GET, data.id], data);
      queryClient.invalidateQueries({ queryKey: [DELIVERABLE_LIST] });
    },
    onError: (error) => {
      showErrorToast(error, "deliverables", "Failed to save deliverable.");
    },
  });
};

/** Rename deliverable metadata. */
export const useUpdateDeliverable = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateDeliverablePayload) => updateDeliverable(id, data),
    onSuccess: (data) => {
      queryClient.setQueryData([DELIVERABLE_GET, id], data);
      queryClient.invalidateQueries({ queryKey: [DELIVERABLE_LIST] });
    },
    onError: (error) => {
      showErrorToast(error, "deliverables", "Failed to save your changes.");
    },
  });
};

export const useDeleteDeliverable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteDeliverable(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: [DELIVERABLE_GET, id] });
      queryClient.invalidateQueries({ queryKey: [DELIVERABLE_LIST] });
      toast.success("Deliverable deleted.");
    },
    onError: (error) => {
      showErrorToast(error, "deliverables", "Failed to delete deliverable.");
    },
  });
};

/**
 * Retry a `FAILED` build: reset the monolith row to `GENERATING`, then
 * enqueue a fresh agent-service job — the same two-step kickoff the composer
 * uses. If the enqueue half fails, the row is left `GENERATING` and the job
 * sweeper eventually fails it again, so the retry becomes available once more
 * rather than sticking.
 */
export const useRetryDeliverableBuild = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const deliverable = await rebuildDeliverable(id);
      try {
        await enqueueDeliverableBuild({ deliverableId: id });
      } catch (error) {
        // The row is already GENERATING; the sweeper fails it again if the
        // build never starts, so the retry becomes available once more.
        showErrorToast(
          error,
          "deliverables",
          "The build was reset, but generation could not be restarted.",
        );
        return deliverable;
      }
      toast.success("Deliverable generation restarted.");
      return deliverable;
    },
    onSuccess: (deliverable) => {
      queryClient.setQueryData([DELIVERABLE_GET, deliverable.id], deliverable);
    },
    onError: (error) => {
      showErrorToast(
        error,
        "deliverables",
        "Failed to restart deliverable generation.",
      );
    },
    onSettled: (_data, _error, id) => {
      queryClient.invalidateQueries({ queryKey: [DELIVERABLE_GET, id] });
      queryClient.invalidateQueries({ queryKey: [DELIVERABLE_LIST] });
    },
  });
};

/**
 * Replaces the whole figure set after a chat edit. The backend flips the
 * deliverable back to `DRAFT` when any figure is `UNSOURCED`, so the detail
 * record is refetched rather than patched from the response.
 */
export const useAppendDeliverableRevision = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (revision: DeliverableRevisionPayload) =>
      appendDeliverableRevision(id, revision),
    onSuccess: (deliverable) => {
      queryClient.setQueryData([DELIVERABLE_GET, id], deliverable);
      queryClient.invalidateQueries({ queryKey: [DELIVERABLE_GET, id] });
      queryClient.invalidateQueries({ queryKey: [DELIVERABLE_LIST] });
    },
    onError: (error) => {
      showErrorToast(error, "deliverables", "Failed to update the figures.");
    },
  });
};
